"use client";

import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import CurationDropFooter from "@/components/waves/drops/CurationDropFooter";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import { WaveDropQuoteDisplayProvider } from "@/components/waves/drops/WaveDropQuoteDisplayContext";
import { ImageScale } from "@/helpers/image.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { useNavigateToDropWave } from "@/hooks/useNavigateToDropWave";
import { useIntersectionObserver } from "@/hooks/scroll/useIntersectionObserver";
import { type RenderComponentProps, useMasonry, usePositioner, useResizeObserver } from "masonic";
import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type ReactElement, type SetStateAction } from "react";
const MASONRY_COLUMN_WIDTH = 300;
const MASONRY_GUTTER = 16;
const INFINITE_SCROLL_ROOT_MARGIN = "1200px 0px";
const SCROLL_IDLE_DELAY_MS = 120;
const CURATION_CARD_CLASS_NAME = "tw-group tw-relative tw-isolate tw-z-0 tw-rounded-xl desktop-hover:hover:tw-z-30 focus-within:tw-z-30";
const CURATION_CARD_HOVER_FRAME_CLASS_NAME = "tw-pointer-events-none tw-absolute tw-inset-0 -tw-z-10 tw-rounded-xl tw-border tw-border-solid tw-border-transparent tw-transition-colors tw-duration-200 tw-ease-out desktop-hover:group-hover:tw-border-white/10 motion-reduce:tw-transition-none";
type PanelViewport = {
  readonly height: number;
  readonly isScrolling: boolean;
  readonly scrollTop: number;
};
interface CommunityCurationsMasonryProps {
  readonly drops: readonly ExtendedDrop[];
  readonly fetchNextPage: () => Promise<void>;
  readonly hasNextPage: boolean | undefined;
  readonly isFetchingNextPage: boolean;
  readonly scrollContainer: HTMLElement | null;
}
const EMPTY_VIEWPORT: PanelViewport = {
  height: 0,
  isScrolling: false,
  scrollTop: 0
};
const noop = () => {};
const getDropKey = (drop: ExtendedDrop) => drop.stableKey;
const getGridScrollTop = (scrollContainer: HTMLElement, gridElement: HTMLElement | null) => {
  if (!gridElement) {
    return 0;
  }
  const scrollRect = scrollContainer.getBoundingClientRect();
  const gridRect = gridElement.getBoundingClientRect();
  const gridOffsetTop = gridRect.top - scrollRect.top + scrollContainer.scrollTop;
  return Math.max(0, scrollContainer.scrollTop - gridOffsetTop);
};
const areViewportsEqual = (left: PanelViewport, right: PanelViewport) => left.height === right.height && left.isScrolling === right.isScrolling && left.scrollTop === right.scrollTop;
const readPanelViewport = (scrollContainer: HTMLElement, gridElement: HTMLElement | null, isScrolling: boolean): PanelViewport => ({
  height: scrollContainer.clientHeight,
  isScrolling,
  scrollTop: getGridScrollTop(scrollContainer, gridElement)
});
const setPanelViewport = (setViewport: Dispatch<SetStateAction<PanelViewport>>, nextViewport: PanelViewport) => {
  setViewport(currentViewport => areViewportsEqual(currentViewport, nextViewport) ? currentViewport : nextViewport);
};
const schedulePanelViewportUpdate = ({
  frameId,
  gridElement,
  isScrolling,
  scrollContainer,
  setViewport
}: {
  readonly frameId: number | null;
  readonly gridElement: HTMLElement | null;
  readonly isScrolling: boolean;
  readonly scrollContainer: HTMLElement;
  readonly setViewport: Dispatch<SetStateAction<PanelViewport>>;
}): number => {
  if (frameId !== null) {
    cancelAnimationFrame(frameId);
  }
  return requestAnimationFrame(() => {
    const nextViewport = readPanelViewport(scrollContainer, gridElement, isScrolling);
    setPanelViewport(setViewport, nextViewport);
  });
};
function useElementWidth(element) {
  const $ = _c(5);
  const [width, setWidth] = useState(0);
  let t0;
  let t1;
  if ($[0] !== element) {
    t0 = () => {
      if (!element || typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(t2 => {
        const [entry] = t2;
        const nextWidth = Math.floor(entry?.contentRect.width ?? 0);
        setWidth(currentWidth => currentWidth === nextWidth ? currentWidth : nextWidth);
      });
      observer.observe(element);
      return () => observer.disconnect();
    };
    t1 = [element];
    $[0] = element;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== width) {
    t2 = {
      setWidth,
      width
    };
    $[3] = width;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function usePanelViewport(scrollContainer, gridElement) {
  const $ = _c(4);
  const [viewport, setViewport] = useState(EMPTY_VIEWPORT);
  let t0;
  let t1;
  if ($[0] !== gridElement || $[1] !== scrollContainer) {
    t0 = () => {
      if (!scrollContainer) {
        return;
      }
      let idleTimeout = null;
      let frameId = null;
      const scheduleViewportUpdate = isScrolling => {
        frameId = schedulePanelViewportUpdate({
          frameId,
          gridElement,
          isScrolling,
          scrollContainer,
          setViewport
        });
      };
      const onScroll = () => {
        scheduleViewportUpdate(true);
        if (idleTimeout) {
          clearTimeout(idleTimeout);
        }
        idleTimeout = setTimeout(() => scheduleViewportUpdate(false), SCROLL_IDLE_DELAY_MS);
      };
      scheduleViewportUpdate(false);
      scrollContainer.addEventListener("scroll", onScroll, {
        passive: true
      });
      const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => scheduleViewportUpdate(false));
      observer?.observe(scrollContainer);
      if (gridElement) {
        observer?.observe(gridElement);
      }
      return () => {
        scrollContainer.removeEventListener("scroll", onScroll);
        observer?.disconnect();
        if (idleTimeout) {
          clearTimeout(idleTimeout);
        }
        if (frameId !== null) {
          cancelAnimationFrame(frameId);
        }
      };
    };
    t1 = [gridElement, scrollContainer];
    $[0] = gridElement;
    $[1] = scrollContainer;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return scrollContainer ? viewport : EMPTY_VIEWPORT;
}
function CommunityCurationsInfiniteScrollTrigger(t0) {
  const $ = _c(5);
  const {
    onIntersection,
    scrollContainer
  } = t0;
  const triggerRef = useRef(null);
  let t1;
  if ($[0] !== onIntersection) {
    t1 = entry => onIntersection(entry.isIntersecting);
    $[0] = onIntersection;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleIntersection = t1;
  let t2;
  if ($[2] !== scrollContainer) {
    t2 = {
      root: scrollContainer,
      rootMargin: INFINITE_SCROLL_ROOT_MARGIN,
      threshold: 0
    };
    $[2] = scrollContainer;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useIntersectionObserver(triggerRef, t2, handleIntersection, Boolean(scrollContainer));
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div ref={triggerRef} aria-hidden="true" className="tw-h-px" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function CommunityCurationsMasonryItem(t0) {
  const $ = _c(12);
  const {
    data: drop
  } = t0;
  const navigateToDropWave = useNavigateToDropWave();
  let t1;
  if ($[0] !== drop) {
    t1 = <CurationDropFooter drop={drop} />;
    $[0] = drop;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== drop || $[3] !== navigateToDropWave || $[4] !== t1) {
    t2 = <Drop drop={drop} previousDrop={null} nextDrop={null} showWaveInfo={true} activeDrop={null} showReplyAndQuote={false} location={DropLocation.MY_STREAM} dropViewDropId={null} onReply={noop} onReplyClick={noop} onQuoteClick={navigateToDropWave} onDropContentClick={navigateToDropWave} footer={t1} mediaImageScale={ImageScale.AUTOx1080} timestampLayout="inline" showInteractions={false} inlineAuthorOnDesktop={true} fullWidthMedia={true} fullWidthLinkPreviews={true} />;
    $[2] = drop;
    $[3] = navigateToDropWave;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== drop.author || $[7] !== t2) {
    t3 = <WaveDropQuoteDisplayProvider flattenWhenAuthorSameAs={drop.author}>{t2}</WaveDropQuoteDisplayProvider>;
    $[6] = drop.author;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div aria-hidden="true" className={CURATION_CARD_HOVER_FRAME_CLASS_NAME} />;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t3) {
    t5 = <article className={CURATION_CARD_CLASS_NAME}>{t3}{t4}</article>;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function CommunityCurationsVirtualMasonry(t0) {
  const $ = _c(19);
  const {
    drops,
    scrollContainer
  } = t0;
  const [gridElement, setGridElement] = useState(null);
  const {
    setWidth,
    width
  } = useElementWidth(gridElement);
  const viewport = usePanelViewport(scrollContainer, gridElement);
  let t1;
  if ($[0] !== drops) {
    t1 = [...drops];
    $[0] = drops;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const items = t1;
  let t2;
  if ($[2] !== items) {
    t2 = items.slice(0, 8).map(getDropKey);
    $[2] = items;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resetKey = t2.join("|");
  const t3 = Math.max(width, 1);
  let t4;
  if ($[4] !== t3) {
    t4 = {
      columnGutter: MASONRY_GUTTER,
      columnWidth: MASONRY_COLUMN_WIDTH,
      rowGutter: MASONRY_GUTTER,
      width: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== items.length || $[7] !== resetKey) {
    t5 = [resetKey, items.length];
    $[6] = items.length;
    $[7] = resetKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const positioner = usePositioner(t4, t5);
  const resizeObserver = useResizeObserver(positioner);
  let t6;
  if ($[9] !== setWidth) {
    t6 = element => {
      setGridElement(element);
      setWidth(element?.offsetWidth ?? 0);
    };
    $[9] = setWidth;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const setContainerRef = t6;
  const t7 = Math.max(viewport.height, 1);
  let t8;
  if ($[11] !== items || $[12] !== positioner || $[13] !== resizeObserver || $[14] !== setContainerRef || $[15] !== t7 || $[16] !== viewport.isScrolling || $[17] !== viewport.scrollTop) {
    t8 = {
      containerRef: setContainerRef,
      height: t7,
      isScrolling: viewport.isScrolling,
      itemHeightEstimate: 420,
      itemKey: getDropKey,
      items,
      overscanBy: 2,
      positioner,
      render: CommunityCurationsMasonryItem,
      resizeObserver,
      scrollTop: viewport.scrollTop
    };
    $[11] = items;
    $[12] = positioner;
    $[13] = resizeObserver;
    $[14] = setContainerRef;
    $[15] = t7;
    $[16] = viewport.isScrolling;
    $[17] = viewport.scrollTop;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return useMasonry(t8) as ReactElement;
}
export default function CommunityCurationsMasonry(t0) {
  const $ = _c(16);
  const {
    drops,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    scrollContainer
  } = t0;
  const shouldShowPaginationFooter = Boolean(hasNextPage) || isFetchingNextPage;
  const shouldShowLoader = isFetchingNextPage || drops.length === 0;
  let t1;
  if ($[0] !== fetchNextPage || $[1] !== hasNextPage || $[2] !== isFetchingNextPage) {
    t1 = isIntersecting => {
      if (!isIntersecting || !hasNextPage || isFetchingNextPage) {
        return;
      }
      fetchNextPage().catch(_temp);
    };
    $[0] = fetchNextPage;
    $[1] = hasNextPage;
    $[2] = isFetchingNextPage;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleIntersection = t1;
  let t2;
  if ($[4] !== drops || $[5] !== scrollContainer) {
    t2 = <CommunityCurationsVirtualMasonry drops={drops} scrollContainer={scrollContainer} />;
    $[4] = drops;
    $[5] = scrollContainer;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== handleIntersection || $[8] !== isFetchingNextPage || $[9] !== scrollContainer || $[10] !== shouldShowLoader || $[11] !== shouldShowPaginationFooter) {
    t3 = shouldShowPaginationFooter && <div className="tw-flex tw-justify-center tw-py-6">{!isFetchingNextPage && <CommunityCurationsInfiniteScrollTrigger onIntersection={handleIntersection} scrollContainer={scrollContainer} />}{shouldShowLoader && <CircleLoader size={CircleLoaderSize.MEDIUM} />}</div>;
    $[7] = handleIntersection;
    $[8] = isFetchingNextPage;
    $[9] = scrollContainer;
    $[10] = shouldShowLoader;
    $[11] = shouldShowPaginationFooter;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== t2 || $[14] !== t3) {
    t4 = <div>{t2}{t3}</div>;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
function _temp() {}